import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import "./AdminDashboard.css";

export default function AdminDashboard() {
  const navigate = useNavigate();

  /* ================= STATE ================= */
  const [activeTab, setActiveTab] = useState("dashboard");
  const [employees, setEmployees] = useState([]);
  const [projects, setProjects] = useState([]);
  const [openings, setOpenings] = useState([]);
  const [impactStats, setImpactStats] = useState([]);
  const [loading, setLoading] = useState(false);

  const [settings, setSettings] = useState({
    companyName: "Meetra Green Energy",
    contactEmail: "admin@meetragreen.com",
    profitMargin: 15,
  });

  const [openingForm, setOpeningForm] = useState({
    title: "",
    location: "Jetpur, Gujarat",
    type: "job",
    experience: "",
    duration: "",
    mode: "On-site",
  });

  /* ================= AUTH GUARD ================= */
  useEffect(() => {
    const user = localStorage.getItem("user");
    if (!user) navigate("/", { replace: true });
  }, [navigate]);

  /* ================= BACK BUTTON LOGOUT ================= */
  useEffect(() => {
    const handleBackButton = () => {
      localStorage.removeItem("user");
      navigate("/", { replace: true });
    };
    window.addEventListener("popstate", handleBackButton);
    return () => window.removeEventListener("popstate", handleBackButton);
  }, [navigate]);

  /* ================= INITIAL LOAD ================= */
  useEffect(() => {
    const loadData = async () => {
      try {
        const [empRes, projRes, openingRes, impactRes] = await Promise.all([
          axios.get("https://meetra-green-energy-backend.onrender.com/api/employees"),
          axios.get("https://meetra-green-energy-backend.onrender.com/api/projects"),
          axios.get("https://meetra-green-energy-backend.onrender.com/api/openings"),
          axios.get("https://meetra-green-energy-backend.onrender.com/api/impact-stats"),
        ]);
        setEmployees(empRes.data);
        setProjects(projRes.data);
        setOpenings(openingRes.data);
        setImpactStats(impactRes.data);
      } catch (err) {
        console.error(err);
      }
    };
    loadData();
  }, []);

  /* ================= FETCH FUNCTIONS ================= */
  const fetchEmployees = async () => {
    setLoading(true);
    try {
      const res = await axios.get("https://meetra-green-energy-backend.onrender.com/api/employees");
      setEmployees(res.data);
      setActiveTab("employees");
    } catch {
      alert("Failed to load employees");
    } finally {
      setLoading(false);
    }
  };

  const fetchProjects = async () => {
    setLoading(true);
    try {
      const res = await axios.get("https://meetra-green-energy-backend.onrender.com/api/projects");
      setProjects(res.data);
      setActiveTab("projects");
    } catch {
      alert("Failed to load projects");
    } finally {
      setLoading(false);
    }
  };

  const fetchOpenings = async () => {
    setLoading(true);
    try {
      const res = await axios.get("https://meetra-green-energy-backend.onrender.com/api/openings");
      setOpenings(res.data);
      setActiveTab("openings");
    } catch {
      alert("Failed to load openings");
    } finally {
      setLoading(false);
    }
  };

  const fetchImpactStats = async () => {
    setLoading(true);
    try {
      const res = await axios.get("https://meetra-green-energy-backend.onrender.com/api/impact-stats");
      setImpactStats(res.data);
      setActiveTab("impact");
    } catch {
      alert("Failed to load impact stats");
    } finally {
      setLoading(false);
    }
  };

  /* ================= DELETE ================= */
  const deleteEmployee = async (id) => {
    if (!window.confirm("Delete this employee?")) return;
    try {
      await axios.delete(`https://meetra-green-energy-backend.onrender.com/api/employees/${id}`);
      setEmployees(employees.filter((e) => e._id !== id));
    } catch {
      alert("Failed to delete employee");
    }
  };

  const deleteProject = async (id) => {
    if (!window.confirm("Delete this project?")) return;
    try {
      await axios.delete(`https://meetra-green-energy-backend.onrender.com/api/projects/${id}`);
      setProjects(projects.filter((p) => p._id !== id));
    } catch {
      alert("Failed to delete project");
    }
  };

  const deleteOpening = async (id) => {
    if (!window.confirm("Delete this opening?")) return;
    try {
      await axios.delete(`https://meetra-green-energy-backend.onrender.com/api/openings/${id}`);
      setOpenings(openings.filter((o) => o._id !== id));
    } catch {
      alert("Failed to delete opening");
    }
  };

  /* ================= OPENING FORM HANDLERS ================= */
  const handleOpeningChange = (e) => {
    setOpeningForm({ ...openingForm, [e.target.name]: e.target.value });
  };

  const handleOpeningSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post("https://meetra-green-energy-backend.onrender.com/api/openings", openingForm);
      alert("Vacancy posted successfully!");
      setOpeningForm({ ...openingForm, title: "", experience: "", duration: "" });
      fetchOpenings();
    } catch {
      alert("Error posting vacancy");
    }
  };

  /* ================= IMPACT HANDLERS ================= */
  const impactLabels = {
    projects: "Solar Projects Completed",
    capacity: "Installed Solar Capacity",
    emissions: "CO₂ Emissions Reduced",
    trees: "Trees Planted",
    team: "Skilled Team Members",
    experience: "Industry Experience",
  };

  const handleImpactUpdate = async (id, target, suffix) => {
    try {
      await axios.put(`https://meetra-green-energy-backend.onrender.com/api/impact-stats/${id}`, { target, suffix });
      alert(`${impactLabels[id]} updated!`);
      fetchImpactStats();
    } catch {
      alert("Impact update failed");
    }
  };

  /* ================= LOGOUT ================= */
  const handleLogout = () => {
    localStorage.removeItem("user");
    navigate("/", { replace: true });
  };

  /* ================= RENDER ================= */
  return (
    <div className="admin-container">
      {/* SIDEBAR */}
      <aside className="admin-sidebar">
        <h2 className="admin-logo">Meetra Admin</h2>
        <ul>
          <li className={activeTab === "dashboard" ? "active" : ""} onClick={() => setActiveTab("dashboard")}>Dashboard</li>
          <li className={activeTab === "employees" ? "active" : ""} onClick={fetchEmployees}>Employees</li>
          <li className={activeTab === "projects" ? "active" : ""} onClick={fetchProjects}>Projects</li>
          <li className={activeTab === "openings" ? "active" : ""} onClick={fetchOpenings}>Job/Openings</li>
          <li className={activeTab === "impact" ? "active" : ""} onClick={fetchImpactStats}>Impact Numbers</li>
          <li className={activeTab === "settings" ? "active" : ""} onClick={() => setActiveTab("settings")}>Settings</li>
        </ul>
        <button className="logout-btn" onClick={handleLogout}>Logout</button>
      </aside>

      {/* MAIN */}
      <main className="admin-main">
        <div className="admin-topbar">
          <h1>Admin Dashboard</h1>
          <span>Welcome, Admin</span>
        </div>

        {/* DASHBOARD */}
        {activeTab === "dashboard" && (
          <div className="dashboard-view">
            <div className="stats-grid">
              <div className="stat-card"><h3>Total Employees</h3><p>{employees.length}</p></div>
              <div className="stat-card"><h3>Total Projects</h3><p>{projects.length}</p></div>
              <div className="stat-card"><h3>Total Openings</h3><p>{openings.length}</p></div>
            </div>
            <div className="action-grid">
              <button onClick={() => navigate("/add-employee")}>Add Employee</button>
              <button onClick={() => navigate("/add-project")}>Add Project</button>
            </div>
          </div>
        )}

        {/* EMPLOYEES */}
        {activeTab === "employees" && (
          <div className="employees-view">
            <div className="view-header">
              <h2>Employees</h2>
              <button onClick={() => navigate("/add-employee")}>+ Add</button>
            </div>
            {loading ? <p>Loading...</p> : (
              <table className="employee-table">
                <thead>
                  <tr><th>ID</th><th>Name</th><th>Email</th><th>Role</th><th>Action</th></tr>
                </thead>
                <tbody>
                  {employees.map(emp => (
                    <tr key={emp._id}>
                      <td>{emp.employeeId}</td>
                      <td>{emp.name}</td>
                      <td>{emp.email}</td>
                      <td><span className="role-badge">{emp.role}</span></td>
                      <td><button className="delete-row-btn" onClick={() => deleteEmployee(emp._id)}>Delete</button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}

        {/* PROJECTS */}
        {activeTab === "projects" && (
          <div className="projects-view">
            <div className="view-header">
              <h2>Projects</h2>
              <button onClick={() => navigate("/add-project")}>+ Add</button>
            </div>
            {loading ? <p>Loading...</p> : (
              <table className="employee-table">
                <thead>
                  <tr><th>Ref ID</th><th>Client Name</th><th>Location</th><th>System Size</th><th>Action</th></tr>
                </thead>
                <tbody>
                  {projects.map(p => (
                    <tr key={p._id}>
                      <td>{p.clientRef}</td>
                      <td>{p.clientName}</td>
                      <td>{p.siteLocation}</td>
                      <td>{p.systemSize}</td>
                      <td><button className="delete-row-btn" onClick={() => deleteProject(p._id)}>Delete</button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}

        {/* JOB / INTERNSHIP OPENINGS */}
        {activeTab === "openings" && (
          <div className="openings-view">
            <h2>Post Job / Internship</h2>
            <form onSubmit={handleOpeningSubmit} style={{ display: "flex", flexDirection: "column", gap: "15px", maxWidth: "600px" }}>
              <input name="title" placeholder="Title" value={openingForm.title} onChange={handleOpeningChange} required />
              <input name="location" placeholder="Location" value={openingForm.location} onChange={handleOpeningChange} />
              <select name="type" value={openingForm.type} onChange={handleOpeningChange}>
                <option value="job">Job</option>
                <option value="internship">Internship</option>
              </select>
              {openingForm.type === "job" ? (
                <input name="experience" placeholder="Experience" value={openingForm.experience} onChange={handleOpeningChange} />
              ) : (
                <>
                  <input name="duration" placeholder="Duration" value={openingForm.duration} onChange={handleOpeningChange} />
                  <input name="mode" placeholder="Mode" value={openingForm.mode} onChange={handleOpeningChange} />
                </>
              )}
              <button type="submit" style={{ padding: "10px", background: "#2ecc71", color: "#fff", border: "none", cursor: "pointer" }}>Post Opening</button>
            </form>

            <h3 style={{ marginTop: "40px" }}>Existing Openings</h3>
            {loading ? <p>Loading...</p> : (
              <table className="employee-table">
                <thead>
                  <tr><th>Title</th><th>Type</th><th>Location</th><th>Experience / Duration</th><th>Mode</th><th>Action</th></tr>
                </thead>
                <tbody>
                  {openings.map(o => (
                    <tr key={o._id}>
                      <td>{o.title}</td>
                      <td>{o.type}</td>
                      <td>{o.location}</td>
                      <td>{o.type === "job" ? o.experience : o.duration}</td>
                      <td>{o.mode}</td>
                      <td><button className="delete-row-btn" onClick={() => deleteOpening(o._id)}>Delete</button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}

        {/* IMPACT STATS */}
        {activeTab === "impact" && (
          <div style={{ padding: "30px", maxWidth: "800px", margin: "0 auto", fontFamily: "Arial" }}>
            <h2 style={{ color: "#2c3e50", borderBottom: "2px solid #27ae60", paddingBottom: "10px" }}>Update Impact Numbers</h2>
            <div style={{ marginTop: "20px" }}>
              {Object.keys(impactLabels).map((key) => {
                const item = impactStats.find((s) => s.id === key) || { target: 0, suffix: "" };
                return (
                  <div key={key} style={{ background: "#f9f9f9", padding: "15px", marginBottom: "10px", borderRadius: "8px", display: "flex", alignItems: "center", justifyContent: "space-between", boxShadow: "0 2px 4px rgba(0,0,0,0.1)" }}>
                    <div style={{ flex: 1 }}>
                      <strong style={{ display: "block", marginBottom: "5px" }}>{impactLabels[key]}</strong>
                      <div style={{ display: "flex", gap: "10px" }}>
                        <input type="number" placeholder="Value" defaultValue={item.target} onChange={(e) => (item.target = e.target.value)} style={{ padding: "8px", width: "100px" }} />
                        <input type="text" placeholder="Suffix (+, kW)" defaultValue={item.suffix} onChange={(e) => (item.suffix = e.target.value)} style={{ padding: "8px", width: "100px" }} />
                      </div>
                    </div>
                    <button onClick={() => handleImpactUpdate(key, item.target, item.suffix)} style={{ backgroundColor: "#27ae60", color: "white", border: "none", padding: "10px 20px", borderRadius: "5px", cursor: "pointer" }}>Save</button>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* SETTINGS */}
        {activeTab === "settings" && (
          <div className="settings-view">
            <h2>System Settings</h2>
            <label>Company Name</label>
            <input value={settings.companyName} onChange={(e) => setSettings({ ...settings, companyName: e.target.value })} />
            <label>Contact Email</label>
            <input value={settings.contactEmail} onChange={(e) => setSettings({ ...settings, contactEmail: e.target.value })} />
            <label>Profit Margin (%)</label>
            <input type="number" value={settings.profitMargin} onChange={(e) => setSettings({ ...settings, profitMargin: e.target.value })} />
            <button className="save-btn">Save Settings</button>
          </div>
        )}
      </main>
    </div>
  );
}